import React, { useMemo, useState } from 'react';

import { Modal } from 'src/components/Modal/Modal';
import { Input } from 'src/components/Input';
import { Button } from 'src/components/Button';
import { Container } from 'src/components/Container';

import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  NOTIFICATION_LIMITS,
  sendAdminNotification,
  type NotificationKind,
} from 'src/api/adminNotifications';

import {
  Callout,
  CharCount,
  CheckboxRow,
  Field,
  FieldFooter,
  FieldLabel,
  FormGrid,
  Textarea,
} from './styled';

export type NotificationTarget =
  | { kind: 'all' }
  | { kind: 'individual'; userId: string; userLabel?: string };

type SendNotificationModalProps = {
  target: NotificationTarget;
  onClose: () => void;
  /** Called after a successful send, e.g. to refresh a feed. */
  onSent?: () => void;
};

const SendNotificationModal: React.FC<SendNotificationModalProps> = ({
  target,
  onClose,
  onSent,
}) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [description, setDescription] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [sending, setSending] = useState(false);

  const dispatch = useDispatch();

  const isBroadcast = target.kind === 'all';
  const trimmedTitle = title.trim();
  const trimmedMessage = message.trim();
  const trimmedDescription = description.trim();

  const overLimit =
    trimmedTitle.length > NOTIFICATION_LIMITS.title ||
    trimmedMessage.length > NOTIFICATION_LIMITS.message ||
    trimmedDescription.length > NOTIFICATION_LIMITS.description;

  const canSend = useMemo(
    () => Boolean(trimmedTitle && trimmedMessage) && !overLimit,
    [trimmedTitle, trimmedMessage, overLimit],
  );

  const handleSubmit = async () => {
    setSending(true);
    try {
      const result = await sendAdminNotification({
        kind: target.kind as NotificationKind,
        title: trimmedTitle,
        message: trimmedMessage,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        ...(isBroadcast ? {} : { user_id: target.userId }),
        // The API rejects send_email on a broadcast, so it only travels with an
        // individual send.
        ...(isBroadcast ? {} : { send_email: sendEmail }),
      });

      const sentTo = isBroadcast
        ? `${result.notified_count ?? 0} user${result.notified_count === 1 ? '' : 's'}`
        : target.userLabel || 'the user';

      dispatch(
        setAppAlert({
          message: isBroadcast
            ? `Notification queued for ${sentTo}.`
            : `Notification sent to ${sentTo}${
                sendEmail ? (result.email_sent ? ' and emailed' : ', but the email failed') : ''
              }.`,
          isSuccess: isBroadcast || !sendEmail || Boolean(result.email_sent),
        }),
      );
      onSent?.();
      onClose();
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to send the notification',
          isSuccess: false,
        }),
      );
    } finally {
      setSending(false);
    }
  };

  const actionButtons = (
    <Container display="flex" gap="0.75rem" justifyContent="flex-end" width="100%">
      <Button text="Cancel" className="outline" onClick={onClose} />
      <Button
        text={sending ? 'Sending...' : isBroadcast ? 'Send to all users' : 'Send notification'}
        className="black"
        disabled={!canSend || sending}
        onClick={handleSubmit}
      />
    </Container>
  );

  return (
    <Modal isOpen onClose={onClose} footer={actionButtons}>
      <Container fontWeight={600} fontSize="1.25rem" paddingBottom="1rem">
        {isBroadcast ? 'Notify all users' : 'Send notification'}
      </Container>
      <FormGrid>
        {isBroadcast ? (
          <Callout $tone="warning">
            This reaches every active user and cannot be recalled. The recipients are
            counted when you send; the notifications are then written in the
            background, so the feed fills in over a few moments.
          </Callout>
        ) : (
          <Callout>
            Sending to <strong>{target.userLabel || target.userId}</strong>.
          </Callout>
        )}

        <Field>
          <Input
            label="Title"
            placeholder="Scheduled maintenance"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <FieldFooter>
            <CharCount $over={trimmedTitle.length > NOTIFICATION_LIMITS.title}>
              {trimmedTitle.length}/{NOTIFICATION_LIMITS.title}
            </CharCount>
          </FieldFooter>
        </Field>

        <Field>
          <FieldLabel htmlFor="notification-message">Message</FieldLabel>
          <Textarea
            id="notification-message"
            placeholder="What the user sees in their notification list."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <FieldFooter>
            <CharCount $over={trimmedMessage.length > NOTIFICATION_LIMITS.message}>
              {trimmedMessage.length}/{NOTIFICATION_LIMITS.message}
            </CharCount>
          </FieldFooter>
        </Field>

        <Field>
          <FieldLabel htmlFor="notification-description">Details (optional)</FieldLabel>
          <Textarea
            id="notification-description"
            placeholder="Shown when the user opens the notification."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FieldFooter>
            <CharCount $over={trimmedDescription.length > NOTIFICATION_LIMITS.description}>
              {trimmedDescription.length}/{NOTIFICATION_LIMITS.description}
            </CharCount>
          </FieldFooter>
        </Field>

        {isBroadcast ? null : (
          <CheckboxRow>
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <span>
              Also email this to the user. The notification is still created if the
              email fails.
            </span>
          </CheckboxRow>
        )}
      </FormGrid>
    </Modal>
  );
};

export default SendNotificationModal;
